import { isoStringToDate } from '../shared/date-format';
import { CompanyStatus } from './Company';
import { ColumnDefinition, EntityInterface } from './Table';

/**
 * A Transaction Request represents a request for a transaction. This occurs
 * when:
 * - A supplier sends a request to a borrower
 * - A borrower starts the checkout process for a supplier using APIs
 *
 * Transaction Requests have the following statuses that each represent the following:
 * - Pending: Initial state when request is first created
 * - Approved: Request has been approved by Transend (borrower has valid credit limit, etc)
 * - Denied: Request has been denied by Transend (borrower has bad credit limit, etc)
 * - Accepted: Request was approved by Transend and Accepted by borrower
 * - Declined: Request was approved by Transend and Declined by borrower
 */

/** Type for transaction request statuses */
export type TransactionRequestStatus =
  | 'pending'
  | 'approved'
  | 'denied'
  | 'accepted'
  | 'declined'
  | 'cancelled';

/**
 * A named plan on a fetched transaction request
 * (`offered_plans` from `/commandcenter/checkout/{role}`).
 */
export interface TransactionRequestPlanOffer {
  plan_id: number;
  name: string;
  description: string;
  apr: number;
  mdr: number;
  duration: number;
  /** Grossed-up / authorized amount for this plan */
  total: number;
  /** Buyer-covered processing fee portion */
  fee: number;
  /** Buyer share of MDR as 0–100 */
  split_percentage: number;
}

/**
 * Wire DTO for transaction requests from checkout endpoints
 * (e.g. `/commandcenter/checkout/borrower`).
 *
 * @note Company names/ids only exist on either side of the buyer/supplier endpoint
 */
export interface TransactionRequestInterface {
  tx_request_id: string | null;
  created_at: string | null;
  expires_at: string | null;
  buyer_company?: string;
  buyer_id?: string;
  supplier_company?: string;
  supplier_id?: string;
  supplier_status: CompanyStatus;
  amount: number;
  /** Server-formatted amount for display */
  amount_label: string;
  status: TransactionRequestStatus;
  /** Authorized / selected plan; null while offer is still open */
  plan_id: number | null;
  plan_desc: string | null;
  plan_dur: number | null;
  /** Named plans offered on this request */
  offered_plans?: TransactionRequestPlanOffer[];
  /** Request-level buyer MDR split % (0–100); null when set per-plan */
  supplier_coverage?: number | null;
  commitment_id: string;
  invoice: string | null;
  po_number: string | null;
  memo: string | null;
  fileIds: number[];
}

/**
 * TransactionRequest class representing a transaction request with specialized getters
 */
export class TransactionRequest implements EntityInterface {
  /**
   * Constructor for the TransactionRequest class
   */
  constructor(
    public tx_request_id: string | null,
    public created_at: Date | null,
    public expires_at: Date | null,
    public buyer_company: string,
    public buyer_id: string,
    public supplier_company: string,
    public supplier_id: string,
    public supplier_status: CompanyStatus,
    public amount: number,
    public amount_label: string,
    public status: TransactionRequestStatus,
    public invoice: string,
    public po_number: string,
    public plan_id: number | null,
    public plan_desc: string,
    public plan_dur: number,
    public commitment_id: string,
    public memo: string,
    public fileIds: number[],
    public plans: TransactionRequestPlanOffer[] = [],
    public supplier_coverage: number | null = null,
    /** Plan the buyer authorized (set locally after accept) */
    public authorized_plan_id: number | null = null,
    /** Total authorized amount (set locally after accept) */
    public authorized_total: number | null = null,
  ) {}

  /**
   * Creates a copy of the current TransactionRequest instance
   */
  copyOf(): TransactionRequest {
    return new TransactionRequest(
      this.tx_request_id,
      this.created_at,
      this.expires_at,
      this.buyer_company,
      this.buyer_id,
      this.supplier_company,
      this.supplier_id,
      this.supplier_status,
      this.amount,
      this.amount_label,
      this.status,
      this.invoice,
      this.po_number,
      this.plan_id,
      this.plan_desc,
      this.plan_dur,
      this.commitment_id,
      this.memo,
      [...this.fileIds],
      this.plans.map((p) => ({ ...p })),
      this.supplier_coverage,
      this.authorized_plan_id,
      this.authorized_total,
    );
  }

  /**
   * Return true if the current TransactionRequest instance is equal to the given request
   */
  equals(other: TransactionRequest): boolean {
    return (
      this.tx_request_id === other.tx_request_id &&
      this.created_at === other.created_at &&
      this.expires_at === other.expires_at &&
      this.buyer_company === other.buyer_company &&
      this.buyer_id === other.buyer_id &&
      this.supplier_company === other.supplier_company &&
      this.supplier_id === other.supplier_id &&
      this.supplier_status === other.supplier_status &&
      this.amount === other.amount &&
      this.amount_label === other.amount_label &&
      this.status === other.status &&
      this.invoice === other.invoice &&
      this.po_number === other.po_number &&
      this.plan_id === other.plan_id &&
      this.plan_desc === other.plan_desc &&
      this.plan_dur === other.plan_dur &&
      this.commitment_id === other.commitment_id &&
      this.memo === other.memo &&
      this.supplier_coverage === other.supplier_coverage &&
      this.authorized_plan_id === other.authorized_plan_id &&
      this.authorized_total === other.authorized_total &&
      this.fileIds.length === other.fileIds.length &&
      this.fileIds.every((id, index) => id === other.fileIds[index]) &&
      this.plans.length === other.plans.length
    );
  }

  /**
   * Returns a string representation of the TransactionRequest instance for filtering
   */
  toString(defs: ColumnDefinition<any>[]): string {
    let str = '';
    for (let col of defs) {
      if (col.toStringTransform != undefined) {
        str += col.toStringTransform((this as any)[col.field]) + '█';
      } else {
        str += (this as any)[col.field];
      }
    }

    return str.toLowerCase();
  }

  /**
   * Gets the status as a titlecase string
   */
  get statusTitleCase(): string {
    return (
      this.status.charAt(0).toUpperCase() + this.status.slice(1).toLowerCase()
    );
  }

  /**
   * Gets the status as @product defines it
   */
  get statusProductFormat(): string {
    switch (this.status) {
      case 'approved':
        return 'Pending';

      default:
        return this.statusTitleCase;
    }
  }

  /**
   * Gets the id field for EntityInterface compatibility
   */
  get id(): string {
    return this.tx_request_id || '';
  }

  /**
   * Gets whether or not borrowers can perform actions on this request
   */
  get allowBorrowerAction(): boolean {
    return this.status === 'approved';
  }

  /**
   * Gets whether or not suppliers can perform actions on this request
   */
  get allowSupplierAction(): boolean {
    return false;
  }

  /** True when more than one plan is offered */
  get isMultiPlan(): boolean {
    return this.plans.length > 1;
  }

  /** Effective plans list */
  get offeredPlans(): TransactionRequestPlanOffer[] {
    return this.plans;
  }
}

/** Normalize offered plans from API, synthesizing a single plan from legacy fields when needed */
function normalizePlans(
  dto: TransactionRequestInterface,
): TransactionRequestPlanOffer[] {
  const raw = dto.offered_plans;
  if (raw && raw.length > 0) {
    return raw.map((p) => ({
      plan_id: p.plan_id,
      name: p.name || p.description || `Plan ${p.plan_id}`,
      description: p.description || '',
      duration: p.duration ?? 0,
      apr: p.apr ?? 0,
      mdr: p.mdr ?? 0,
      total: p.total ?? dto.amount,
      fee: p.fee ?? 0,
      split_percentage: p.split_percentage ?? dto.supplier_coverage ?? 0,
    }));
  }

  if (dto.plan_id != null) {
    return [
      {
        plan_id: dto.plan_id,
        name: dto.plan_desc || `Plan ${dto.plan_id}`,
        description: dto.plan_desc || '',
        duration: dto.plan_dur || 0,
        apr: 0,
        mdr: 0,
        total: dto.amount,
        fee: 0,
        split_percentage: dto.supplier_coverage ?? 0,
      },
    ];
  }

  return [];
}

/** Mapper for converting between TransactionRequestInterface and TransactionRequest */
export class TransactionRequestMapper {
  static toDto(request: TransactionRequest): TransactionRequestInterface {
    return {
      tx_request_id: request.tx_request_id,
      created_at: request.created_at?.toISOString() || null,
      expires_at: request.expires_at?.toISOString() || null,
      buyer_company: request.buyer_company,
      buyer_id: request.buyer_id,
      supplier_company: request.supplier_company,
      supplier_id: request.supplier_id,
      supplier_status: request.supplier_status,
      amount: request.amount,
      amount_label: request.amount_label,
      status: request.status,
      plan_id: request.plan_id,
      plan_desc: request.plan_desc || null,
      plan_dur: request.plan_dur || null,
      offered_plans: request.plans,
      supplier_coverage: request.supplier_coverage,
      commitment_id: request.commitment_id,
      invoice: request.invoice,
      po_number: request.po_number,
      memo: request.memo,
      fileIds: request.fileIds,
    };
  }

  static toModel(dto: TransactionRequestInterface): TransactionRequest {
    const plans = normalizePlans(dto);
    return new TransactionRequest(
      dto.tx_request_id,
      isoStringToDate(dto.created_at),
      isoStringToDate(dto.expires_at),
      dto.buyer_company || '',
      dto.buyer_id || '',
      dto.supplier_company || '',
      dto.supplier_id || '',
      dto.supplier_status,
      dto.amount,
      dto.amount_label
        ? dto.amount_label
        : new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(dto.amount),
      dto.status,
      dto.invoice || '',
      dto.po_number || '',
      dto.plan_id ?? null,
      dto.plan_desc || plans[0]?.description || plans[0]?.name || '',
      dto.plan_dur ?? plans[0]?.duration ?? 0,
      dto.commitment_id || '',
      dto.memo || '',
      dto.fileIds || [],
      plans,
      dto.supplier_coverage ?? null,
      dto.plan_id ?? null,
      plans.find((p) => p.plan_id === dto.plan_id)?.total ?? null,
    );
  }
}
